'use strict';

import {Point2D} from '../util/Geometry.js';
import * as util from '../util/Common.js';
import {EventManager} from '../util/EventManager.js';
import * as domUtil from '../util/Dom.js';

const privateMap = util.createPrivateMapper();

export class Bounds {
  constructor(){
    const pv = privateMap(this);
    pv.position = new Point2D(0, 0);
    pv.size = new Point2D(0, 0);
  }
  get position(){
    return privateMap(this).position;
  }
  get size(){
    return privateMap(this).size;
  }
}

export class BoxDisposition {
  constructor(){
    const pv = privateMap(this);
    pv.offset = new Point2D(0, 0);

    this.angle = 0;
    this.zoom = 1;
  }
  get offset(){
    return privateMap(this).offset;
  }

  get state(){
    const pv = privateMap(this);
    const _self = this;
    return {
      offset: pv.offset,
      zoom: _self.zoom,
      angle: _self.angle,
      equals: function(o) {
        if (!o) {
          return false;
        }
        return this.offset.equals(o.offset) && this.zoom == o.zoom && this.angle == o.angle;
      }
    }
  }
}

const calcDeltaZoom = function(pz, delta, factor){
  if (!pz) {
    return;
  }
  const zlsep = Math.floor(Math.log(pz) / Math.log(2) * factor);
  let nz;
  let i = 0;
  if (delta > 0) {
    do {
      nz = Math.pow(2, (zlsep + (i++)) / factor);
    } while (nz <= pz);
  } else {
    do {
      nz = Math.pow(2, (zlsep - (i++)) / factor);
    } while (nz >= pz);
  }
  return nz;
};

const calculateFitScale = function(boxSize, size, dir, ang){
  const angle = ang ? ang : 0
  const sz = boxSize;
  const vsz = size;
  const vr = vsz.x / vsz.y;
  const ir = sz.x / sz.y;
  let fsc;

  // let d = vsz.y / Math.cos(Math.PI / 2 - (sz.angle() + angle))
  const da = sz.angle()
  let normAng = angle
  while (normAng > Math.PI * 2) {
    normAng = normAng - Math.PI * 2
  }
  while (normAng < 0) {
    normAng = normAng + Math.PI * 2
  }
  if (normAng > Math.PI) {
    normAng = 2 * Math.PI - normAng
  }
  if (normAng > Math.PI / 2) {
    normAng = Math.PI - normAng
  }

  // normAng = Math.abs(normAng)
  // if (normAng > Math.PI / 2) {
  //   normAng = Math.PI - normAng
  // }

  const hd = vsz.x / Math.cos((da - normAng))
  const hfsc = Math.abs(hd * Math.sin(da) / sz.y)
  const vd = vsz.y / Math.sin((da + normAng))
  const vfsc = Math.abs(vd * Math.cos(da) / sz.x)

  if (dir == 'H') {
    fsc = hfsc
  } else if (dir == 'V') {
    fsc = vfsc
  } else {
    fsc = Math.min(hfsc, vfsc)
  }

  // if ((dir != null && dir != 'H') || (dir == null && ir < vr)) {
  //   fsc = Math.min(1, vsz.y / sz.y);
  // } else {
  //   fsc = Math.min(1, vsz.x / sz.x);
  // }
  return fsc;
};

export class BoxContentControl extends EventManager {
  constructor(){
    super();
    const pv = privateMap(this);
    pv.boxSize = new Point2D(1, 1);
    pv.boundaryBoxSize = null;
    pv.viewSize = new Point2D(1, 1);
    pv.center = pv.viewSize.div(2);
    pv.disposition = new BoxDisposition();
    this.minZoom = 0.01;
    this.maxZoom = 1;
    pv.viewMargin = new Point2D();

    pv.calculateZoomRange = () => {
      // minZoom = Math.min(viewSize.x / boxSize.x, viewSize.y / boxSize.y);
      const boxSize = pv.boundaryBoxSize ? pv.boundaryBoxSize : pv.boxSize
      this.minZoom = Math.min(pv.viewSize.x, pv.viewSize.y) / boxSize.distance();
    };
    pv.zoom = (z) => {
      pv.disposition.zoom = this.normalizeZoom(z);
    };
    pv.move = (px) => {
      pv.disposition.offset.set(this.normalizeOffset(px));
    };
    pv.movePoint = () => {
      const base = new Point2D(arguments);
      const pt = base.sub(boxSize.div(2)).mul(pv.disposition.zoom).negate();
      pv.disposition.offset.set(this.normalizeOffset(pt));
    };
    pv.rotate = function(ang){
      pv.disposition.angle = ang;
    };
  }

  get disposition(){
    return privateMap(this).disposition;
  }

  set viewMargin(p){
    const pv = privateMap(this);
    pv.viewMargin.set(p);
  };
  set viewSize(sz){
    const pv = privateMap(this);
    if (pv.viewSize.equals(sz)) {
      return
    }
    pv.viewSize.set(sz);
    pv.center.set(pv.viewSize.div(2));
    pv.calculateZoomRange();
    pv.zoom(pv.disposition.zoom);
    pv.move(pv.disposition.offset);
  };
  get viewSize(){
    return privateMap(this).viewSize;
  }

  set boxSize(sz){
    const pv = privateMap(this);
    pv.boxSize.set(sz);
    pv.calculateZoomRange();
  };
  get boxSize(){
    return privateMap(this).boxSize;
  }

  set boundaryBoxSize(sz){
    const pv = privateMap(this);
    pv.boundaryBoxSize = sz
    pv.calculateZoomRange();
  }
  get boundaryBoxSize(){
    return privateMap(this).boundaryBoxSize;
  }

  set resource(resource) {
    const pv = privateMap(this);
    if (!resource) {
      return;
    }
    pv.boxSize.set(resource.bounds.size);
    pv.boundaryBoxSize = resource.boundaryBoxSize;
    pv.calculateZoomRange();
  }

  normalizeZoom(z){
    return Math.max(this.minZoom, Math.min(this.maxZoom, z));
  };

  normalizeOffset(pt){
    const pv = privateMap(this);
    const viewMargin = new Point2D(pv.viewMargin.x * pv.viewSize.x, pv.viewMargin.y * pv.viewSize.y);
    const vcp = pv.center;
    const angle = pv.disposition.angle;
    const rpt = pt.rotate(-angle);
    const zcp = pv.boxSize.mul(pv.disposition.zoom / 2);
    // const zcp = (pv.boundaryBoxSize ? pv.boundaryBoxSize : pv.boxSize).mul(pv.disposition.zoom / 2);

    const vca = zcp.angle();

    const rbd = new Point2D(
      Math.max(Math.abs(Math.cos(vca - angle)), Math.abs(Math.cos(-vca - angle))),
      Math.max(Math.abs(Math.sin(vca - angle)), Math.abs(Math.sin(-vca - angle)))
    ).mul(zcp.distance());

    if (rbd.x + viewMargin.x < vcp.x) {
      rpt.x = 0;
    } else {
      const dx = (rbd.x - vcp.x) - Math.abs(rpt.x) + viewMargin.x;
      if (dx < 0) {
        rpt.x += (rpt.x < 0 ? -1 : 1) * dx;
      }
    }
    if (rbd.y + viewMargin.y < vcp.y) {
      rpt.y = 0;
    } else {
      const dy = (rbd.y - vcp.y) - Math.abs(rpt.y) + viewMargin.y;
      if (dy < 0) {
        rpt.y += (rpt.y < 0 ? -1 : 1) * dy;
      }
    }
    const ret = rpt.rotate(pv.disposition.angle);

    return ret;
  };

  zoom(z){
    const pv = privateMap(this);
    pv.zoom(z)
    pv.move(pv.disposition.offset);
  };
  deltaZoom(delta, zoomFactor) {
    const pv = privateMap(this);
    this.zoom(calcDeltaZoom(pv.disposition.zoom, delta, zoomFactor));
  };
  move(){
    const pv = privateMap(this);
    pv.move(new Point2D(arguments));
  };
  rotate(ang){
    const pv = privateMap(this);
    pv.rotate(ang);
    pv.move(pv.disposition.offset);
  };
  pointZoom(z, pt){
    if (!z) {
      return;
    }
    const pv = privateMap(this);
    const pz = pv.disposition.zoom;
    const ppt = pv.disposition.offset;
    const angle = pv.disposition.angle;
    const nz = this.normalizeZoom(z);

    if (pz !== nz) {
      const r = nz / pz;
      // const npt = ppt.mul(r).sub(pt.mul(r).sub(pt));
      const npt = (ppt.rotate(-angle).mul(r).sub(pt.mul(r).sub(pt))).rotate(angle);
      pv.zoom(nz);
      pv.move(npt);
    }
  };
  pointDeltaZoom(delta, zoomFactor, pt) {
    const pv = privateMap(this);
    this.pointZoom(calcDeltaZoom(pv.disposition.zoom, delta, zoomFactor), pt ? pt : pv.disposition.center);
  };
  moveBy() {
    const pv = privateMap(this);
    this.move(pv.disposition.offset.add(new Point2D(arguments).rotate(pv.disposition.angle)));
  };
  rotateBy(ang){
    const pv = privateMap(this);
    this.rotate(pv.disposition.angle + ang);
  };
  getViewPoint(px){
    const pv = privateMap(this);
    return px.sub(pv.center);
  };
  getBoxPoint(px){
    const pv = privateMap(this);
    const vp = this.getViewPoint(px);
    return vp.rotate(pv.disposition.angle).sub(pv.disposition.offset).div(pv.disposition.zoom).add(pv.boxSize.div(2));
  };
  getViewPixel(pt){
    const pv = privateMap(this);
    return pt.sub(pv.boxSize.div(2)).mul(pv.disposition.zoom).add(pv.disposition.offset).rotate(-pv.disposition.angle).add(pv.center);
  };
  getZoomRange(){
    return [this.minZoom, this.maxZoom];
  };
  calcFitZoom(dir){
    const pv = privateMap(this);
    let fsc = calculateFitScale(pv.boxSize, pv.viewSize, dir, pv.disposition.angle);
    return fsc
  };
  fit(dir){
    const pv = privateMap(this);
    let fsc = calculateFitScale(pv.boxSize, pv.viewSize, dir, pv.disposition.angle);
    pv.zoom(fsc);
    pv.move(new Point2D());
  };
  fitRect(rect){
    const pv = privateMap(this);
    const mid = rect.size.div(2);
    // const diagDist = mid.distance();
    // const diagAng = mid.angle();
    // const sz = new Point2D(Math.cos(angle - diagAng), Math.sin(diagAng + angle)).abs().mul(2 * diagDist);
    const sz = mid.rotate(pv.disposition.angle).abs().max(new Point2D(mid.x, -mid.y).rotate(pv.disposition.angle).abs()).mul(2);
    const vsz = pv.viewSize;
    const vr = vsz.x / vsz.y;
    const ir = sz.x / sz.y;
    let fsc;
    if (ir < vr) {
      fsc = Math.min(1, vsz.y / sz.y);
    } else {
      fsc = Math.min(1, vsz.x / sz.x);
    }
    pv.zoom(fsc);
    pv.movePoint(rect.center());
  };
}

class Region {
  constructor (data){
    const rect = data.xywh;
    this.rect = rect;
    this.x = rect.x;
    this.y = rect.y;
    this.width = rect.width;
    this.height = rect.height;
    this.data = data;
  }
  contains(pt) {
    const ex = this.x + this.width;
    const ey = this.y + this.height;
    return pt.x >= this.x && pt.y >= this.y && pt.x <= ex && pt.y <= ey;
  }
  draw(ctx, offset, zoom) {
    const zsz = new Point2D(this.width, this.height).mul(zoom);
    const lt = new Point2D(this.x, this.y).mul(zoom).add(offset);
    ctx.strokeRect(lt.x, lt.y, zsz.x, zsz.y);
  }
};

export class Annotation2DListResource {
  constructor(id){
    const pv = privateMap(this);
    pv.id = id;
    pv.offset = new Point2D();
    pv.size = new Point2D();

    pv.gridUnit = 6;
    pv.grids = [];
    pv.regionDatas = [];
    pv.regions = [];
    pv.idDic = {};
    pv.renderRegions = {};
    pv.regionSeq = 0;

    pv.transformGrid = function(cell, offset){
      const tgu = pv.gridUnit;
      const grids = pv.grids;
      const region = new Region(cell);
      const rngIdx = pv.regions.length;
      pv.regions[rngIdx] = region;
      pv.idDic[cell.id] = region;
      region.x += offset.x;
      region.y += offset.y;

      const sx = region.x;
      const ex = sx + region.width;
      const sy = region.y;
      const ey = sy + region.height;
      const sxi = sx >> tgu;
      const syi = sy >> tgu;
      const exi = ex >> tgu;
      const eyi = ey >> tgu;

      for (let xi = sxi; xi <= exi; xi++) {
        for (let yi = syi; yi <= eyi; yi++) {
          if (!grids[xi]) {
            grids[xi] = [];
          }
          if (!grids[xi][yi]) {
            grids[xi][yi] = [];
          }
          grids[xi][yi][grids[xi][yi].length] = rngIdx;
        }
      }
      if (cell.children) {
        const cl = cell.children.length;
        for (let i = 0; i < cl; i++) {
          const child = cell.children[i];
          if (!child) {
            continue;
          }
          transformGrid(child, offset);
        }
      }
      return region;
    };

    pv.assignId = function(data){
      data.id = pv.regionSeq;
      pv.regionSeq++;
      if (data.children) {
        const cl = data.children.length;
        for (let i = 0; i < cl; i++) {
          assignId(data.children[i]);
        }
      }
    };
    pv.getRegionByPixel = function(grids, regions, px) {
      const ret = [];
      const tgu = pv.gridUnit;
      const xi = px.x >> tgu;
      const yi = px.y >> tgu;
      if (grids[xi] && grids[xi][yi]) {
        const g = grids[xi][yi];
        const l = g.length;
        for (let i = 0; i < l; i++) {
          const region = regions[g[i]];
          if (region.contains(px)) {
            ret[ret.length] = region.data.id;
          }
        }
      }
      return ret;
    };
  }
  get size(){
    return privateMap(this).size.clone();
  }
  set size(size){
    privateMap(this).size.copy(size); 
  }

  get offset(){
    return privateMap(this).offset.clone();
  }
  set offset(offset){
    if (!offset) {
      privateMap(this).offset.set(0, 0)
    }
    privateMap(this).offset.copy(offset); 
    this.refreshGrid()
  }

  get id(){
    return privateMap(this).id;
  };

  refreshGrid(){
    const pv = privateMap(this);
    pv.grids.length = 0;
    pv.regions.length = 0;
    pv.idDic = {};
    const l = pv.regionDatas.length;
    for (let i = 0; i < l; i++) {
      pv.transformGrid(pv.regionDatas[i], pv.offset);
      // pv.transformGrid(pv.regionDatas[i], new Point2D());
    }
  };

  addRegion(data){
    const pv = privateMap(this);
    pv.assignId(data);
    pv.regionDatas[pv.regionDatas.length] = data;
    let r;
    if (data.xywh) {
      r = pv.transformGrid(data, pv.offset);
      // r = pv.transformGrid(data, new Point2D());
    }
    return r;
  }

  addRegions(datas){
    const pv = privateMap(this);
    const l = datas.length;
    // const targetRegions = [];
    for (let i = 0; i < l; i++) {
      const data = datas[i];
      if (!data) {
        continue;
      }
      this.addRegion(data);
      // regionDatas[regionDatas.length] = data;
      // transformGrid(data, new Point2D());

      // const range = new Region(data.xywh, data.resource);
      // gridContext.cells[anno.cellIndex] = range;
      // regionDatas[regionDatas.length] = data;
      // targetRegions[targetRegions.length] = range;
    }
    // transformGrid(targetRegions, new Point2D());
  }
  removeRegion(id){
    const pv = privateMap(this);
    const regionDatas = pv.regionDatas;

    const l = regionDatas.length;
    let deleteIndex = -1;
    pv.renderRegions = {};
    for (let i = 0; i < l; i++) {
      if (regionDatas[i].id == id) {
        deleteIndex = i;
      }
    }
    if (deleteIndex >= 0) {
      regionDatas.splice(deleteIndex, 1);
    }
    this.refreshGrid();
  };
  setRenderRegions(renderType, target){
    privateMap(this).renderRegions[renderType] = target;
  }
  getRenderRegions(){
    return privateMap(this).renderRegions;
  }
  getRegionByPixel(px){
    const pv = privateMap(this);
    return pv.getRegionByPixel(pv.grids, pv.regions, px);
  }
  getDatas(){
    const pv = privateMap(this);
    return pv.regionDatas;
  }
  getRegions(){
    const pv = privateMap(this);
    return pv.regions;
  }
  getRegion(regionId){
    const pv = privateMap(this);
    return pv.idDic[regionId];
  };
}

export class Annotation2DListRenderer {
  constructor(options){
    const pv = privateMap(this);
    pv.styles = options.styles;
  }
  render(view, resource, control, session){
    // const resource = pv.resource;
    if (!view || !resource || !control) {
      return;
    }
    const disposition = control.box.disposition;
    const pv = privateMap(this);
    const ctx = view.context2d;
    const vc = view.center;
    const z = disposition.zoom;
    const ofs = vc.add(disposition.offset.sub(resource.size.mul(z / 2)));
    // const imgOfs = resource.size.mul(z / 2);
    // const cells = gridContext.cells;

    ctx.save();

    const rotateCenter = vc;
    ctx.translate(rotateCenter.x, rotateCenter.y);
    ctx.rotate(disposition.angle);
    ctx.translate(-rotateCenter.x, -rotateCenter.y);

    // const cells = layer.getRegions();
    const renderRegions = resource.getRenderRegions();

    for (let k in renderRegions) {
      const renderIds = renderRegions[k];
      if (!renderIds) {
        continue;
      }
      ctx.save();
      const style = pv.styles[k];
      if (style) {
        style(ctx);
      }
      const rl = renderIds.length;
      for (let ri = 0; ri < rl; ri++) {
        const region = resource.getRegion(renderIds[ri]);
        if (!region) {
          continue;
        }
        // const zsz = new Point2D(region.width, region.height).mul(z);
        // const lt = new Point2D(region.x, region.y).mul(z).sub(imgOfs).add(vc.add(ofs));
        // ctx.strokeRect(lt.x, lt.y, zsz.x, zsz.y);
        region.draw(ctx, ofs, z);
      }
      ctx.restore();
    }
    ctx.restore();
  }
}
